import React from 'react';

const TooltipWrapper: React.FC<{ text: string | number | null | undefined }> = ({ text }) => {
  return (
      <div className='relative group w-full flex items-center justify-start text-left'>
        <div className='max-w-[180px] truncate whitespace-nowrap overflow-hidden'>
          {text ?? '-'}
        </div>
        <div className='absolute z-[9999] hidden group-hover:flex flex-col items-center'>
          <div className='relative'>
            <div className='absolute bottom-full px-3 py-2 text-sm text-white bg-gray-900 rounded-md shadow-lg w-max max-w-xs whitespace-normal'>
              {text}
            </div>
          </div>
        </div>
      </div>
  );
};

const toNumber = (value: number | string | undefined | null): number | null => {
  if (value === undefined || value === null) return null;
  const num = typeof value === 'number' ? value : Number(value);
  return isNaN(num) ? null : num;
};

const numberCell = (value: number | string | undefined | null, decimals = 2) => {
  const num = toNumber(value);
  return (
      <span className='text-sm text-slate-600'>
        {num !== null ? num.toFixed(decimals) : '-'}
      </span>
  );
};

interface PsychometricApplicant {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export type PsychometricResult = {
  applicant?: PsychometricApplicant;
  participantId?: string;
  email?: string;
  gender?: string;
  birthDate?: string;
  testDate?: string;
  age?: number | string;
  ageCategory?: string;

  // FCAT
  gca?: number | string;
  gf?: number | string;   // Managers only
  gc?: number | string;   // Managers only
  gv?: number | string;   // Managers only
  gs?: number | string;   // Managers only
  gsm?: number | string;  // Managers only

  ds?: number | string;

  // OPTI
  grit?: number | string;
  growthMindset?: number | string;
  interpersonal?: number | string;
  eq?: number | string;

  // EAST
  eastTasks?: (number | string)[]; // 20 values, TPX01-EAST..TPX20-EAST
  eastRataRata?: number | string;
  eastJumlah?: number | string;
  eastSalah?: number | string;
  eastSimpangan?: number | string;
  eastTinggi?: number | string;
  eastPuncak?: number | string;

  // Managers only
  incritsPercentile?: number | string;
};

export const getPsychometricColumns = (psychometricType: 'Staff' | 'Managers') => {
  const baseColumns = [
    {
      key: 'name',
      label: 'Name',
      width: '170px',
      align: 'left' as const,
      disableSort: true,
      render: (row: PsychometricResult) => {
        const fullName = [row.applicant?.firstName, row.applicant?.lastName]
            .filter(Boolean)
            .join(' ');
        return (
            <div className='flex flex-col'>
              <span className='text-sm font-medium text-slate-800'>{fullName || '-'}</span>
              <span className='text-xs text-slate-400'>{row.applicant?.email ?? row.email}</span>
            </div>
        );
      },
    },
    {
      key: 'participantId',
      label: 'ID',
      width: '90px',
      align: 'left' as const,
      render: (row: PsychometricResult) => <TooltipWrapper text={row.participantId} />,
    },
    {
      key: 'gender',
      label: 'Gender',
      width: '70px',
      align: 'center' as const,
      render: (row: PsychometricResult) => <TooltipWrapper text={row.gender} />,
    },
    {
      key: 'birthDate',
      label: 'Birth Date',
      width: '100px',
      align: 'left' as const,
      render: (row: PsychometricResult) => <TooltipWrapper text={row.birthDate} />,
    },
    {
      key: 'testDate',
      label: 'Test Date',
      width: '100px',
      align: 'left' as const,
      render: (row: PsychometricResult) => <TooltipWrapper text={row.testDate} />,
    },
    {
      key: 'age',
      label: 'Age',
      width: '60px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.age) ?? 0,
      render: (row: PsychometricResult) => (
          <span className='text-sm text-slate-700'>{toNumber(row.age) ?? '-'}</span>
      ),
    },
    {
      key: 'ageCategory',
      label: 'Age Cat.',
      width: '80px',
      align: 'center' as const,
      render: (row: PsychometricResult) => <TooltipWrapper text={row.ageCategory} />,
    },
    {
      key: 'gca',
      label: 'GCA',
      width: '70px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.gca) ?? 0,
      render: (row: PsychometricResult) => (
          <span className='text-sm font-semibold text-slate-700'>
            {toNumber(row.gca)?.toFixed(2) ?? '-'}
          </span>
      ),
    },
  ];

  // Managers-only FCAT sub-scores
  const managerFcatColumns = [
    {
      key: 'gf',
      label: 'GF',
      width: '70px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.gf) ?? 0,
      render: (row: PsychometricResult) => numberCell(row.gf),
    },
    {
      key: 'gc',
      label: 'GC',
      width: '70px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.gc) ?? 0,
      render: (row: PsychometricResult) => numberCell(row.gc),
    },
    {
      key: 'gv',
      label: 'GV',
      width: '70px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.gv) ?? 0,
      render: (row: PsychometricResult) => numberCell(row.gv),
    },
    {
      key: 'gs',
      label: 'GS',
      width: '70px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.gs) ?? 0,
      render: (row: PsychometricResult) => numberCell(row.gs),
    },
    {
      key: 'gsm',
      label: 'GSM',
      width: '70px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.gsm) ?? 0,
      render: (row: PsychometricResult) => numberCell(row.gsm),
    },
  ];

  const dsColumn = {
    key: 'ds',
    label: 'DS',
    width: '70px',
    align: 'center' as const,
    sortValue: (row: PsychometricResult) => toNumber(row.ds) ?? 0,
    render: (row: PsychometricResult) => numberCell(row.ds, 1),
  };

  const optiColumns = [
    {
      key: 'grit',
      label: 'Grit',
      width: '70px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.grit) ?? 0,
      render: (row: PsychometricResult) => numberCell(row.grit),
    },
    {
      key: 'growthMindset',
      label: 'Growth Mindset',
      width: '100px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.growthMindset) ?? 0,
      render: (row: PsychometricResult) => numberCell(row.growthMindset),
    },
    {
      key: 'interpersonal',
      label: 'Interpersonal',
      width: '100px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.interpersonal) ?? 0,
      render: (row: PsychometricResult) => numberCell(row.interpersonal),
    },
    {
      key: 'eq',
      label: 'EQ',
      width: '70px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.eq) ?? 0,
      render: (row: PsychometricResult) => numberCell(row.eq),
    },
  ];

  // EAST: one column per task, TPX01-EAST .. TPX20-EAST
  const eastTaskColumns = Array.from({ length: 20 }, (_, i) => ({
    key: `eastTask_${i + 1}`,
    label: `T${String(i + 1).padStart(2, '0')}`,
    width: '60px',
    align: 'center' as const,
    sortValue: (row: PsychometricResult) => toNumber(row.eastTasks?.[i]) ?? 0,
    render: (row: PsychometricResult) => numberCell(row.eastTasks?.[i], 1),
  }));

  const eastSummaryColumns = [
    {
      key: 'eastRataRata',
      label: 'Rata-rata',
      width: '85px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.eastRataRata) ?? 0,
      render: (row: PsychometricResult) => numberCell(row.eastRataRata),
    },
    {
      key: 'eastJumlah',
      label: 'Jumlah',
      width: '75px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.eastJumlah) ?? 0,
      render: (row: PsychometricResult) => numberCell(row.eastJumlah, 1),
    },
    {
      key: 'eastSalah',
      label: 'Salah',
      width: '70px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.eastSalah) ?? 0,
      render: (row: PsychometricResult) => numberCell(row.eastSalah, 1),
    },
    {
      key: 'eastSimpangan',
      label: 'Simpangan',
      width: '90px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.eastSimpangan) ?? 0,
      render: (row: PsychometricResult) => numberCell(row.eastSimpangan, 4),
    },
    {
      key: 'eastTinggi',
      label: 'Tinggi',
      width: '70px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.eastTinggi) ?? 0,
      render: (row: PsychometricResult) => numberCell(row.eastTinggi, 0),
    },
    {
      key: 'eastPuncak',
      label: 'Puncak',
      width: '75px',
      align: 'center' as const,
      sortValue: (row: PsychometricResult) => toNumber(row.eastPuncak) ?? 0,
      render: (row: PsychometricResult) => numberCell(row.eastPuncak, 0),
    },
  ];

  const incritsColumn = {
    key: 'incritsPercentile',
    label: 'INCRITS %ile',
    width: '100px',
    align: 'center' as const,
    sortValue: (row: PsychometricResult) => toNumber(row.incritsPercentile) ?? 0,
    render: (row: PsychometricResult) => {
      const num = toNumber(row.incritsPercentile);
      return (
          <span className='text-sm font-medium text-slate-700'>
            {num !== null ? `${num.toFixed(2)}%` : '-'}
          </span>
      );
    },
  };

  if (psychometricType === 'Managers') {
    return [
      ...baseColumns,
      ...managerFcatColumns,
      dsColumn,
      ...optiColumns,
      ...eastTaskColumns,
      ...eastSummaryColumns,
      incritsColumn,
    ];
  }

  // Staff: no GF/GC/GV/GS/GSM, no INCRITS
  return [
    ...baseColumns,
    dsColumn,
    ...optiColumns,
    ...eastTaskColumns,
    ...eastSummaryColumns,
  ];
};